import _objectSpread from "/usr/app/node_modules/@babel/runtime/helpers/esm/objectSpread2.js";
import _regenerator from "/usr/app/node_modules/@babel/runtime/helpers/esm/regenerator.js";
import _asyncToGenerator from "/usr/app/node_modules/@babel/runtime/helpers/esm/asyncToGenerator.js";
import _toConsumableArray from "/usr/app/node_modules/@babel/runtime/helpers/esm/toConsumableArray.js";
import React from "react";
var __jsx = React.createElement;
import { useEffect, useRef, useState } from "react";
import useBoolean from "@hooks/useBoolean";
import { useTranslation } from "@i18n";
import Slider from "@/components/UiKit/Slider/Slider";
import { CDN_LINK } from "@globalConfig";
import { Icon } from "@syarahtech/ui";
import { twMerge } from "tailwind-merge";
import SAR from "@componentsShared/SAR/SAR";
import CancelUpgradeWarranty from "@/pages/tracking/components/SharedComponents/DetailsComponent/components/WarrantyUpsellComponent/CancelUpgradeWarranty";
import Markdown from "markdown-to-jsx";
import useIsMobile from "@hooks/useIsMobile";
import { useLang } from "@/common/Contexts/LanguageContext";
import { api__getWarrantyDetails } from "@/apis/orderTracking/api__getOrderTracking";
import { trackEvents } from "@/common/events/events";
var PriceColumn = function PriceColumn(_ref) {
  var item = _ref.item,
    freeLabel = _ref.freeLabel;
  var isFree = item.price.current === 0;
  if (isFree) {
    return /*#__PURE__*/React.createElement("div", {
      className: "flex flex-1 flex-col items-center pb-[3px]"
    }, /*#__PURE__*/React.createElement("p", {
      className: "text-xs leading-4 text-[#00143c] text-center whitespace-nowrap pt-[13px]"
    }, freeLabel));
  }
  return /*#__PURE__*/React.createElement("div", {
    className: "flex flex-1 flex-col items-center pb-[3px]"
  }, item.price.original && /*#__PURE__*/React.createElement("div", {
    className: "flex gap-px items-center opacity-80 mb-[-3px]"
  }, /*#__PURE__*/React.createElement(SAR, {
    className: "text-[#6d7a8d]"
  }), /*#__PURE__*/React.createElement("p", {
    className: "text-xs leading-4 line-through text-[#6d7a8d] tracking-[-0.3px] whitespace-nowrap"
  }, item.price.original)), /*#__PURE__*/React.createElement("div", {
    className: "flex gap-0.5 items-center mb-[-3px]"
  }, /*#__PURE__*/React.createElement(SAR, {
    className: "text-success"
  }), /*#__PURE__*/React.createElement("p", {
    className: "font-bold text-base leading-6 text-success text-end"
  }, item.price.current)));
};

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
var WarrantyUpsellComponent = function WarrantyUpsellComponent(_ref2) {
  var _items$find, _items$find2, _warrantyV2$selected, _apiSelected$years_wi, _apiSelected$years_wi2, _apiSelected$years_wi3;
  var warrantyV2 = _ref2.warrantyV2,
    onExtend = _ref2.onExtend,
    onCancelExtension = _ref2.onCancelExtension,
    onCancelSuccess = _ref2.onCancelSuccess,
    warrantyPriceDetailsV3 = _ref2.warrantyPriceDetailsV3,
    isBnpl = _ref2.isBnpl,
    order_id = _ref2.order_id,
    user_id = _ref2.user_id,
    referral_location = _ref2.referral_location,
    order_type = _ref2.order_type;
  var _useTranslation = useTranslation(),
    tShort = _useTranslation.tShort;
  var _useState = useState(false),
    isFetchingDetails = _useState[0],
    setIsFetchingDetails = _useState[1];
  var _useState2 = useState(false),
    isExpanded = _useState2[0],
    setIsExpanded = _useState2[1];
  var confirmCancel = useBoolean();
  var isCancelling = useBoolean();
  var _useState3 = useState(function () {
      var _warrantyV2$items$fin, _warrantyV2$items$fin2;
      return (_warrantyV2$items$fin = warrantyV2 === null || warrantyV2 === void 0 ? void 0 : (_warrantyV2$items$fin2 = warrantyV2.items.find(function (item) {
        return item.selected;
      })) === null || _warrantyV2$items$fin2 === void 0 ? void 0 : _warrantyV2$items$fin2.id) !== null && _warrantyV2$items$fin !== void 0 ? _warrantyV2$items$fin : null;
    }),
    selectedId = _useState3[0],
    setSelectedId = _useState3[1];
  var _useState4 = useState(0),
    sliderEdgePadPct = _useState4[0],
    setSliderEdgePadPct = _useState4[1];
  var _useState5 = useState(warrantyV2),
    localWarrantyV2 = _useState5[0],
    setLocalWarrantyV2 = _useState5[1];
  var _useState6 = useState(warrantyPriceDetailsV3),
    localCurrent = _useState6[0],
    setLocalCurrent = _useState6[1];
  var isMobile = useIsMobile();
  var lang = useLang();
  var optionRef = useRef(null);

  // After the panel expands, measure the options row width and compute
  // the edge-padding % so each slider mark aligns with the center of its button.
  // Layout: options row is full-width (W), slider wrapper adds px-4 (16px each side),
  // and the Slider component itself adds px-2.5 (10px each side) for the thumb.
  // Combined track offset from options-row left = 16 + 10 = 26px.
  useEffect(function () {
    if (!isExpanded || !optionRef.current || !localWarrantyV2) return;
    var W = optionRef.current.offsetWidth;
    var n = localWarrantyV2.items.length;
    if (n < 2 || W === 0) return;
    var GAP_PX = 8; // gap-2
    var TRACK_OFFSET_PX = 26; // px-4 (16) + Slider px-2.5 (10)
    var buttonWidth = (W - (n - 1) * GAP_PX) / n;
    var trackWidth = W - 2 * TRACK_OFFSET_PX;
    var pct = (buttonWidth / 2 - TRACK_OFFSET_PX) / trackWidth * 100;
    setSliderEdgePadPct(Math.max(0, pct));
  }, [isExpanded, warrantyV2]);
  if (!localWarrantyV2) return null;
  var items = localWarrantyV2.items;
  var current = localCurrent;
  var selectedItem = (_items$find = items.find(function (item) {
    return item.id === selectedId;
  })) !== null && _items$find !== void 0 ? _items$find : null;
  var defaultItem = (_items$find2 = items.find(function (item) {
    return item.is_default;
  })) !== null && _items$find2 !== void 0 ? _items$find2 : items[0];
  var isNonDefaultSelected = selectedItem ? !selectedItem.is_default : false;
  var apiSelected = localWarrantyV2.selected;
  // CTA is hidden when the user's current selection matches what the API says is selected
  var isUnchangedFromApi = selectedId === (apiSelected === null || apiSelected === void 0 ? void 0 : apiSelected.id);
  var highestItem = _toConsumableArray(items).sort(function (a, b) {
    return b.price.current - a.price.current;
  })[0];
  var isHighestWarrantySelected = !!apiSelected && !!highestItem && apiSelected.id === highestItem.id;
  // Downgrade: user picked an item cheaper than the API-selected one
  var isDowngrade = !!selectedItem && !!apiSelected && selectedItem.price.current < apiSelected.price.current;
  var handleCancelExtension = /*#__PURE__*/function () {
    var _ref3 = _asyncToGenerator(/*#__PURE__*/_regenerator().m(function _callee() {
      var result, _result$warrantyV2$it, _result$warrantyV, _result$warrantyV2, _newDefault$id, _apiSelected$years$to, _apiSelected$years, newDefault;
      return _regenerator().w(function (_context) {
        while (1) switch (_context.n) {
          case 0:
            if (defaultItem) {
              _context.n = 1;
              break;
            }
            return _context.a(2);
          case 1:
            isCancelling.setTrue();
            _context.n = 2;
            return onCancelExtension === null || onCancelExtension === void 0 ? void 0 : onCancelExtension();
          case 2:
            result = _context.v;
            isCancelling.setFalse();
            if (result) {
              if (result.warrantyV2) setLocalWarrantyV2(result.warrantyV2);
              if (result.warrantyPriceDetailsV3) setLocalCurrent(result.warrantyPriceDetailsV3);
              newDefault = (_result$warrantyV2$it = (_result$warrantyV = result.warrantyV2) === null || _result$warrantyV === void 0 ? void 0 : _result$warrantyV.items.find(function (i) {
                return i.is_default;
              })) !== null && _result$warrantyV2$it !== void 0 ? _result$warrantyV2$it : (_result$warrantyV2 = result.warrantyV2) === null || _result$warrantyV2 === void 0 ? void 0 : _result$warrantyV2.items[0];
              setSelectedId((_newDefault$id = newDefault === null || newDefault === void 0 ? void 0 : newDefault.id) !== null && _newDefault$id !== void 0 ? _newDefault$id : defaultItem.id);
              onCancelSuccess === null || onCancelSuccess === void 0 ? void 0 : onCancelSuccess((_apiSelected$years$to = apiSelected === null || apiSelected === void 0 ? void 0 : (_apiSelected$years = apiSelected.years) === null || _apiSelected$years === void 0 ? void 0 : _apiSelected$years.toString()) !== null && _apiSelected$years$to !== void 0 ? _apiSelected$years$to : "");
            }
            confirmCancel.setFalse();
          case 3:
            return _context.a(2);
        }
      }, _callee);
    }));
    return function handleCancelExtension() {
      return _ref3.apply(this, arguments);
    };
  }();

  // Slider: each item maps to its index as the slider value
  var sliderMarks = items.map(function (item, idx) {
    return _objectSpread({
      value: idx,
      label: item.title
    }, item.selected && {
      currentLabel: tShort("warranty/current_label", {
        ns: "web_tracking"
      })
    });
  });
  var idToSliderValue = function idToSliderValue(id) {
    var idx = items.findIndex(function (item) {
      return item.id === id;
    });
    return idx !== -1 ? idx : 0;
  };
  var handleSliderChange = function handleSliderChange(val) {
    var item = items[val];
    if (item) setSelectedId(item.id);
  };
  var handleOptionSelect = function handleOptionSelect(item) {
    setSelectedId(item.id);
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: "flex flex-col gap-2 p-3 rounded-lg w-full",
    style: {
      backgroundImage: "linear-gradient(-68deg, rgb(255, 210, 94) 10%, rgb(227, 182, 67) 99%)"
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex flex-1 flex-col gap-4 items-end min-w-0"
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex items-end justify-center w-full"
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex flex-1 flex-col gap-2 justify-center min-w-0"
  }, /*#__PURE__*/React.createElement("div", {
    className: "bg-[#f4edfc] flex items-center justify-center overflow-hidden px-2 rounded-[36px] w-[81px] py-1"
  }, /*#__PURE__*/React.createElement("p", {
    className: "font-bold text-[10px] text-[#0d0db5] text-end whitespace-nowrap"
  }, tShort("warranty/badge", {
    ns: "web_tracking"
  }))), /*#__PURE__*/React.createElement("div", {
    className: "flex flex-col gap-0.5 w-full"
  }, isBnpl && warrantyV2 !== null && warrantyV2 !== void 0 && (_warrantyV2$selected = warrantyV2.selected) !== null && _warrantyV2$selected !== void 0 && _warrantyV2$selected.coverage_percentage ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("p", {
    className: "font-bold text-base md:text-[18px] leading-6 text-[#0d0db5] tracking-[-0.4px] w-full text-start"
  }, tShort("warranty/coverage_title", {
    ns: "web_tracking",
    coverage_percentage: warrantyV2.selected.coverage_percentage
  })), /*#__PURE__*/React.createElement("p", {
    className: "font-normal text-xs md:text-sm leading-4 text-[#00143c] tracking-[-0.3px] w-full text-start"
  }, tShort("warranty/coverage_subtitle", {
    ns: "web_tracking"
  }))) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("p", {
    className: "font-bold text-base md:text-[18px] leading-6 text-[#0d0db5] tracking-[-0.4px] w-full text-start"
  }, tShort("warranty/car_title", {
    ns: "web_tracking"
  })), /*#__PURE__*/React.createElement("p", {
    className: "font-normal text-xs md:text-sm leading-4 text-[#00143c] tracking-[-0.3px] w-full text-start"
  }, tShort("warranty/car_description", {
    ns: "web_tracking"
  }))))), /*#__PURE__*/React.createElement("div", {
    className: "relative shrink-0",
    style: {
      width: 91,
      height: 72
    }
  }, /*#__PURE__*/React.createElement("img", {
    src: CDN_LINK + "/assets/images/car-shield.svg",
    alt: "",
    className: "absolute inset-0 w-full h-full object-contain"
  }))), /*#__PURE__*/React.createElement("div", {
    className: "bg-[#fbfbfb] border border-[#e0e0e0] flex flex-col items-start overflow-hidden rounded-lg w-full"
  }, !isExpanded ?
  /*#__PURE__*/
  /* ── Collapsed state ─────────────────────────────────────── */
  React.createElement("div", {
    className: "flex flex-col w-full"
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex items-center justify-between px-3 py-2 w-full flex-wrap gap-4 md:flex-nowrap"
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex gap-0.5 items-center"
  }, apiSelected !== null && apiSelected !== void 0 && (_apiSelected$years_wi = apiSelected.years_with_kilometers) !== null && _apiSelected$years_wi !== void 0 && _apiSelected$years_wi.title ? /*#__PURE__*/React.createElement("div", {
    className: "flex items-center gap-1 text-[#00143c] whitespace-nowrap"
  }, /*#__PURE__*/React.createElement(Icon, {
    icon: "material-symbols--add-moderator-outline-rounded",
    className: "text-violet"
  }), /*#__PURE__*/React.createElement("p", {
    className: "text-sm font-medium leading-6 text-[#00143C]"
  }, tShort("remaining/car_warranty", {
    ns: "web_tracking"
  })), /*#__PURE__*/React.createElement(Markdown, {
    className: "text-xs font-medium text-[#6b7ea5] leading-4 p-0.5 rounded",
    style: {
      color: apiSelected === null || apiSelected === void 0 ? void 0 : (_apiSelected$years_wi2 = apiSelected.years_with_kilometers) === null || _apiSelected$years_wi2 === void 0 ? void 0 : _apiSelected$years_wi2.text_color,
      backgroundColor: apiSelected === null || apiSelected === void 0 ? void 0 : (_apiSelected$years_wi3 = apiSelected.years_with_kilometers) === null || _apiSelected$years_wi3 === void 0 ? void 0 : _apiSelected$years_wi3.highlight_background
    }
  }, apiSelected.years_with_kilometers.title)) : /*#__PURE__*/React.createElement("div", {
    className: "flex items-center gap-1 text-[#00143c] whitespace-nowrap"
  }, /*#__PURE__*/React.createElement(Icon, {
    icon: "material-symbols--add-moderator-outline-rounded",
    className: "text-violet"
  }), /*#__PURE__*/React.createElement("p", {
    className: "text-sm font-medium leading-6 text-[#00143C]"
  }, tShort("remaining/car_warranty", {
    ns: "web_tracking"
  })), /*#__PURE__*/React.createElement("div", {
    className: "bg-violet-light px-1 rounded"
  }, /*#__PURE__*/React.createElement("p", {
    className: "text-violet text-sm font-bold leading-6"
  }, current === null || current === void 0 ? void 0 : current.title)))), /*#__PURE__*/React.createElement("button", {
    onClick: function onClick() {
      setIsExpanded(true);
      trackEvents('warranty_list_opened', {
        order_id: order_id,
        referral_location: referral_location,
        order_type: order_type
      });
    },
    className: twMerge("flex items-center justify-center min-h-[32px] cursor-pointer border-0 outline-none shrink-0", isHighestWarrantySelected ? "bg-transparent p-1" : "bg-[#0d0db5] px-2 py-1 rounded md:min-w-[76px] w-full md:w-fit")
  }, isHighestWarrantySelected ? /*#__PURE__*/React.createElement(Icon, {
    icon: "material-symbols--edit-square-outline-rounded",
    className: "text-[#0d0db5] text-xl"
  }) : /*#__PURE__*/React.createElement("span", {
    className: "font-bold text-xs text-white whitespace-nowrap"
  }, tShort("warranty/cta_expand", {
    ns: "web_tracking"
  }))))) :
  /*#__PURE__*/
  /* ── Expanded state ──────────────────────────────────────── */
  React.createElement("div", {
    className: "flex flex-col gap-5 items-start px-2 py-6 w-full"
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex flex-col gap-4 items-start w-full"
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex gap-2 items-center w-full",
    ref: optionRef
  }, items.map(function (item) {
    var isSelected = selectedId === item.id || selectedId === null && item.selected;
    return /*#__PURE__*/React.createElement("div", {
      key: item.id,
      className: "relative flex flex-1 self-stretch justify-center"
    }, item.badge && /*#__PURE__*/React.createElement("div", {
      className: "absolute -top-[11px] left-1/2 -translate-x-1/2 bg-[#8e47e0] flex items-center justify-center overflow-hidden px-1 py-0.5 rounded z-[2] whitespace-nowrap"
    }, /*#__PURE__*/React.createElement("p", {
      className: "font-normal text-[10px] text-white tracking-[-0.25px]"
    }, item.badge.text)), /*#__PURE__*/React.createElement("button", {
      onClick: function onClick() {
        return handleOptionSelect(item);
      },
      className: twMerge("flex flex-1 flex-col items-center justify-center w-full rounded-lg border cursor-pointer outline-none transition-colors py-1 px-1 bg-[#fbfbfb] border-gray-gainsboro border-solid", item.badge && "pt-3 pb-1 px-1", isSelected && "bg-[#e7e7f8] border-[#0d0db5]")
    }, /*#__PURE__*/React.createElement("div", {
      className: "flex flex-col items-center"
    }, /*#__PURE__*/React.createElement("span", {
      className: "text-sm leading-5 tracking-[-0.35px] font-bold text-[#00143c] whitespace-nowrap"
    }, item.title), /*#__PURE__*/React.createElement("p", {
      className: "font-normal text-[11px] text-[#6b7ea5] whitespace-nowrap tracking-[-0.3px] leading-6"
    }, item.subtitle))), items.length === 2 && item.selected && /*#__PURE__*/React.createElement("div", {
      className: "absolute pointer-events-none flex flex-col items-center",
      style: {
        top: "100%",
        left: "50%",
        transform: "translateX(-50%)",
        marginTop: 8,
        zIndex: 20
      }
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        width: 0,
        height: 0,
        borderLeft: "6px solid transparent",
        borderRight: "6px solid transparent",
        borderBottom: "6px solid #0d0db5"
      }
    }), /*#__PURE__*/React.createElement("span", {
      className: "bg-[#0d0db5] text-white text-[10px] font-medium leading-4 tracking-[-0.25px] px-2 py-1 rounded whitespace-nowrap"
    }, tShort("warranty/ur_current_warranty", {
      ns: "web_tracking"
    }))));
  })), items && items.length > 2 && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: "w-full md:px-4 px-5 en:px-8"
  }, /*#__PURE__*/React.createElement(Slider, {
    value: idToSliderValue(selectedId),
    onChange: handleSliderChange,
    marks: sliderMarks,
    reverse: lang == 'ar',
    paddedEdge: sliderEdgePadPct > 0,
    paddedEdgePercent: 0,
    className: "pb-2 pt-1"
  }))), /*#__PURE__*/React.createElement("div", {
    className: twMerge("flex items-end justify-between w-full", items.length === 2 ? "pt-8" : "pt-3")
  }, items.map(function (item) {
    return /*#__PURE__*/React.createElement(PriceColumn, {
      key: item.id,
      item: item,
      freeLabel: tShort("warranty/free", {
        ns: "web_tracking"
      })
    });
  })), selectedItem ? /*#__PURE__*/React.createElement("div", {
    className: "flex flex-col gap-3 items-center w-full "
  }, /*#__PURE__*/React.createElement("p", {
    className: "font-medium text-xs leading-4 text-[#0d0db5] w-full text-center tracking-[-0.3px]"
  }, selectedItem.description), /*#__PURE__*/React.createElement("p", {
    className: "text-[#B0B0B0] text-[10px] font-normal leading-4 text-center"
  }, tShort("warranty/vas_not_included", {
    ns: "web_tracking"
  })), /*#__PURE__*/React.createElement("p", {
    className: "font-normal text-xs leading-4 text-[#b0b0b0] w-full text-center tracking-[-0.3px]"
  }, tShort("warranty/slider_hint", {
    ns: "web_tracking"
  }))) : /*#__PURE__*/React.createElement("p", {
    className: "font-normal text-xs leading-4 text-[#b0b0b0] w-full text-center tracking-[-0.3px]"
  }, tShort("warranty/slider_hint", {
    ns: "web_tracking"
  }))), selectedId && selectedItem && !isUnchangedFromApi && /*#__PURE__*/React.createElement("button", {
    disabled: isFetchingDetails,
    onClick: /*#__PURE__*/_asyncToGenerator(/*#__PURE__*/_regenerator().m(function _callee2() {
      var _selectedItem$years;
      var details;
      return _regenerator().w(function (_context2) {
        while (1) switch (_context2.n) {
          case 0:
            details = null;
            if (!order_id) {
              _context2.n = 2;
              break;
            }
            setIsFetchingDetails(true);
            _context2.n = 1;
            return api__getWarrantyDetails(order_id, selectedId);
          case 1:
            details = _context2.v;
            setIsFetchingDetails(false);
          case 2:
            trackEvents('warranty_selected', {
              order_id: order_id,
              referral_location: referral_location,
              order_type: order_type,
              selected_warranty: (_selectedItem$years = selectedItem.years) !== null && _selectedItem$years !== void 0 ? _selectedItem$years : selectedId
            });
            onExtend === null || onExtend === void 0 ? void 0 : onExtend(selectedId, details);
          case 3:
            return _context2.a(2);
        }
      }, _callee2);
    })),
    className: "bg-[#0d0db5] flex items-center justify-between px-3 py-2 rounded cursor-pointer border-0 outline-none w-full disabled:opacity-50"
  }, /*#__PURE__*/React.createElement("span", {
    className: "font-bold text-sm text-white text-end whitespace-nowrap"
  }, isDowngrade ? tShort("warranty/cta_change", {
    ns: "web_tracking"
  }) : tShort("warranty/cta_confirm", {
    ns: "web_tracking"
  })), isFetchingDetails ? /*#__PURE__*/React.createElement("div", {
    className: "animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"
  }) : /*#__PURE__*/React.createElement(Icon, {
    icon: "material-symbols--arrow-left-alt-rounded",
    className: "text-white en:rotate-180"
  }))))), !isExpanded && isNonDefaultSelected && !isBnpl && localWarrantyV2.show_cancel_warranty_btn && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: "flex items-center gap-1 justify-center cursor-pointer",
    onClick: confirmCancel.setTrue
  }, /*#__PURE__*/React.createElement(Icon, {
    icon: "mingcute--refresh-3-line",
    className: "text-[#F22C2C]"
  }), /*#__PURE__*/React.createElement("p", {
    className: "text-xs font-bold text-[#F22C2C] leading-4"
  }, tShort("warranty/cancel_extension", {
    ns: "web_tracking"
  }))))), /*#__PURE__*/React.createElement(CancelUpgradeWarranty, {
    isOpen: confirmCancel.value,
    onClose: confirmCancel.setFalse
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex flex-col gap-8 pt-10 pb-4"
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex flex-col gap-4"
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex items-center justify-center"
  }, isMobile ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("img", {
    src: CDN_LINK + "/assets/images/professional-car-protected.svg",
    alt: ""
  })) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("img", {
    src: CDN_LINK + "/assets/images/professional-car.png",
    alt: ""
  }))), /*#__PURE__*/React.createElement("h3", {
    className: "text-primary-new text-base md:text-2xl font-bold text-center"
  }, tShort("warranty/cancel_warranty_title")), /*#__PURE__*/React.createElement("p", {
    className: "text-black-primary text-xs md:text-xl font-normal leading-8 text-center"
  }, tShort("warranty/cancel_warranty_sub_title"))), /*#__PURE__*/React.createElement("div", {
    className: "flex items-center flex-col md:flex-row gap-4 md:gap-6 w-full md:max-w-[370px] mx-auto"
  }, /*#__PURE__*/React.createElement("button", {
    onClick: confirmCancel.setFalse,
    className: "w-full bg-[#0d0db5] text-white font-medium text-base md:text-sm py-3 rounded cursor-pointer border-0 outline-none"
  }, tShort("warranty/cancel_keep", {
    ns: "web_tracking"
  })), /*#__PURE__*/React.createElement("button", {
    onClick: handleCancelExtension,
    disabled: isCancelling.value,
    className: "w-full bg-transparent text-[#e53935] border-0 font-medium text-base md:text-sm py-3 rounded cursor-pointer md:border md:border-solid md:border-[#e53935] outline-none disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#e53935] transition-colors duration-200 ease-in hover:text-white"
  }, tShort("warranty/cancel_confirm", {
    ns: "web_tracking"
  }))))));
};
export default WarrantyUpsellComponent;